import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, SpinningButton } from "Components/ReactstrapSlim";
import { InputFL, ValidationSummary } from "Components/Inputs";
import { useApiClient } from "Services/API/useApiClient";
import { AccountClient } from "Services/LegacyAPI/AccountClient";

type ChangeEmailFormModel = Pick<LSWebUI.Models.ChangeEmailModel, "NewEmail" | "ConfirmEmail">;

const ChangePasswordFormSchema: yup.SchemaOf<ChangeEmailFormModel> = yup.object({
    NewEmail: yup.string()
        .required("New email is required")
        .email("Email is not valid")
        .defined(),
    ConfirmEmail: yup.string()
        .required("Confirmation email is required")
        .oneOf([yup.ref("NewEmail"), null], "Emails must match")
        .defined(),
}).defined();

export interface ChangeEmailFormProps extends LSWebUI.Models.ChangeEmailModel {
    isActive: boolean;
    returnUrl?: string,
}

export const ChangeEmailForm: React.FC<ChangeEmailFormProps> = function ({ isActive, returnUrl, ...changeEmailFormProps }) {
    const accountClient = useApiClient(AccountClient);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [serverErrors, setServerErrors] = useState<Array<[string, string]>>([]);

    const formMethods = useForm<ChangeEmailFormModel>({
        resolver: yupResolver(ChangePasswordFormSchema),
        mode: "onBlur"
    });

    const onSubmit = async () => {
        if (isLoading)
            return;
        setIsLoading(true);
        setServerErrors([]);
        try {
            const isValid = await formMethods.trigger();
            if (isValid) {
                const formValues = formMethods.getValues();

                const changeEmailRequest: LSWebUI.Models.ChangeEmailModel = { 
                    MemberId: changeEmailFormProps.MemberId,
                    ForceEmailChangeToken: changeEmailFormProps.ForceEmailChangeToken, 
                    ...formValues 
                };
                const changeResult = await accountClient.changeEmail(changeEmailRequest);
                if (changeResult.success) {
                    setIsSubmitted(true);
                }
                else {
                    if (changeResult.errors && changeResult.errors.length)
                        setServerErrors(changeResult.errors.map(err => ["newEmail", err]));
                    else
                        setServerErrors([["newEmail", "Unexpected server error"]]);
                }
            }
        } catch (err: any) {
            if (err.error) {
                setServerErrors([["newEmail", err.error]]);
            }
            console.log(err);
        } finally {
            setIsLoading(false);
        }
    }

    const submitOnEnterHandler = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            onSubmit();
        }
    }

    useEffect(() => {
        if (isActive) {
            setTimeout(function () {
                formMethods.setFocus("NewEmail");
            }, 500);
        }
    }, [isActive])

    return (
        <div className="signing-mode-inner signing-mode-inner--centered">
        {
            isSubmitted ? <>
                <div className="change-email-confirmation">
                    <div className="change-email-confirmation-icon"></div>
                    <div className="change-email-confirmation-text">Your email address has been successfully changed. Please sign in again to confirm.</div>
                    <Button className="change-email-confirmation-cta" onClick={() => window.location.href = returnUrl ?? "/" } color="primary" size="md" fluid>Got it</Button>
                </div>
            </>
            : <>
                <div className="signing-header">
                    Set a new email
                </div>
                <div className="signing-caption">
                    <div>If you have any questions, contact <a href="mailto:support@liquidspace.com">support@liquidspace.com</a></div>
                </div>
                <div className="signing-change-email-form">
                    <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverErrors} className="text-center" />
                    <InputFL control={formMethods.control} name="NewEmail" placeholder="New Email Address" aria-label="new email address" className="scef-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />
                    <InputFL control={formMethods.control} name="ConfirmEmail" placeholder="Repeat New Email Address" aria-label="repeat new email address" className="scef-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />

                    <SpinningButton isLoading={isLoading} onClick={onSubmit} color="primary" size="md" fluid className="scef-cta">Set new email address</SpinningButton>
                </div>
            </> 
        }
        </div>
    )
}